import type { ReactNode } from "react";
import type { Accent } from "../lib/api";
import { fmtRelative } from "../lib/format";

const ACCENT_HEADER: Record<Accent, string> = {
  amber: "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200",
  emerald: "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200",
  sky: "bg-sky-100 text-sky-900 dark:bg-sky-500/15 dark:text-sky-200",
  violet: "bg-violet-100 text-violet-900 dark:bg-violet-500/15 dark:text-violet-200",
  rose: "bg-rose-100 text-rose-900 dark:bg-rose-500/15 dark:text-rose-200",
  slate: "bg-slate-100 text-slate-900 dark:bg-slate-500/15 dark:text-slate-100",
  lime: "bg-lime-100 text-lime-900 dark:bg-lime-500/15 dark:text-lime-200",
  orange: "bg-orange-100 text-orange-900 dark:bg-orange-500/15 dark:text-orange-200",
};

interface Props {
  title: string;
  unit?: string;
  accent?: Accent;
  updatedAt?: string | null;
  /** Right-aligned slot for controls (toggles, segmented controls). */
  controls?: ReactNode;
  /** Slot below the chart, e.g. for the legend or a disclaimer. */
  footer?: ReactNode;
  children: ReactNode;
}

export function ChartCard({
  title,
  unit,
  accent = "slate",
  updatedAt,
  controls,
  footer,
  children,
}: Props) {
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      <header
        className={`px-4 py-2.5 flex items-baseline justify-between gap-3 border-b border-slate-200 dark:border-slate-800 ${ACCENT_HEADER[accent]}`}
      >
        <div className="min-w-0">
          <h2 className="font-semibold text-sm sm:text-base truncate">{title}</h2>
          {updatedAt && (
            <p className="text-[10px] opacity-70 truncate">
              Stand: {fmtRelative(updatedAt)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {controls}
          {unit && <span className="text-xs font-mono opacity-80">{unit}</span>}
        </div>
      </header>
      <div className="px-3 sm:px-4 py-3">{children}</div>
      {footer && (
        <div className="px-3 sm:px-4 pb-3 pt-1 border-t border-slate-100 dark:border-slate-800/60">
          {footer}
        </div>
      )}
    </section>
  );
}
